'use client';

import React from 'react';
import Image from 'next/image';
import moment from 'moment';
import { useSearchParams } from 'next/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Virtual } from 'swiper/modules';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import DeckIcon from '@mui/icons-material/Deck';
import SpaIcon from '@mui/icons-material/Spa';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CelebrationIcon from '@mui/icons-material/Celebration';
import RoofingIcon from '@mui/icons-material/Roofing';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import LuggageIcon from '@mui/icons-material/Luggage';
import MarketingBanner from '@/components/company-detail/marketing';
import { CompanyValueType } from '@/types/company-detail';
import ResultChart from '@/components/culture-preference/ResultChart';
import CityCard from '@/components/seo/city/CityCard';
import JobOpportunityCard from '@/components/seo/JobOpportunityCard';
import SubscribeJob from '@/components/subscribe-job';
import setAlertValue from '../Alert';
import {
  COMPANY_SIZE_MAP,
  COMPANY_FINANCING_STAGE_MAP,
  REST_TYPE_MAP,
  WORK_OVERTIME_TYPE_MAP,
  AGE_MAP,
} from '@/constants/company-detail';
import { joinStrFromList } from '@/utils/common-utils';
import { useBodyClassName } from '@/utils/hooks/useBodyClassName';
import { SEARCH_TYPE } from '@/constants/common';
import { EXPERIMENT_COMPANY_DETAIL_GROUP_0 } from '@/constants/ab-testing-config';
import defaultCompanyLogo from '@/assets/company-detail/default-logo.svg';

const BENEFITS_MAP: Record<string, React.ReactNode> = {
  ['五险一金']: <BeachAccessIcon />,
  ['带薪年假']: <DeckIcon />,
  ['定期体检']: <SpaIcon />,
  ['交通补助']: <LocalTaxiIcon />,
  ['通讯补贴']: <PhoneAndroidIcon />,
  ['补充医疗保险']: <MedicalServicesIcon />,
  ['全勤奖']: <EmojiEventsIcon />,
  ['股票期权']: <EmojiEventsIcon />,
  ['餐补']: <RestaurantIcon />,
  ['节日福利']: <CelebrationIcon />,
  ['住房补贴']: <RoofingIcon />,
  ['年终奖']: <MilitaryTechIcon />,
  ['员工旅游']: <LuggageIcon />,
};

const CompanyDetail = (props: CompanyValueType & { testValue: string }) => {
  const {
    name,
    logo,
    location,
    size,
    financing_stage: financingStage,
    description,
    benefits,
    culture_preference: { type: culturePreferenceType },
    team_executives: teamExecutives,
    work_schedule: workSchedule,
    industry,
    medias: { images, videos },
    products,
    job_opportunity: opportunityJobs = [],
    related_jobs: relatedJobs = [],
    cities = [],
    id,
    testValue,
  } = props;
  const searchParams = useSearchParams();
  const flag = searchParams?.get('flag');
  const letter =
    testValue === EXPERIMENT_COMPANY_DETAIL_GROUP_0
      ? {
          title: `${name}正在热招，别因简历错过好机会`,
          subTitle:
            '根据谈职 2024 数据，只有2%的简历能拿到面试机会——你会是那2%吗？',
        }
      : {
          title: '好机会不等人',
          subTitle: '你的经历值得被好公司看到，我们帮你用简历讲出竞争力',
        };

  const renderCompanyCard = (
    data: Record<string, string>[],
    isProduct = true,
  ) => {
    return data.map((item, index) => (
      <div
        key={item.name + index}
        className="h-41 flex items-start gap-6 cursor-pointer rounded-lg bg-white border border-solid border-blue-200 px-9 py-6"
      >
        <div className="relative rounded-full w-16 h-16">
          <Image
            src={item.photo}
            alt={isProduct ? 'product photo' : 'executive photo'}
            fill
            style={{
              objectFit: 'contain',
            }}
          />
        </div>
        <div>
          <p className="text-base font-semibold mb-4 truncate w-48">
            {item.name}
            {`${item.position ? ' / ' + item.position : ''}`}
          </p>
          {item.age_level ? (
            <p className="mb-2 text-sm font-normal">
              {AGE_MAP[item.age_level]}
            </p>
          ) : (
            <></>
          )}
          <div
            dangerouslySetInnerHTML={{
              __html: item.description as string,
            }}
            className="line-clamp-2 font-normal text-sm mb-2 w-48"
          />
          {item.link && (
            <p className="text-sm font-normal truncate w-48">{item.link}</p>
          )}
        </div>
      </div>
    ));
  };

  const renderBenefits = () => {
    return benefits.map((item, index) => (
      <div
        key={item.name + index}
        className="rounded-lg px-6 py-6 font-medium text-sm border border-black/[0.06]"
      >
        <div className="mb-3">{BENEFITS_MAP[item.name]}</div>
        <p className="font-medium text-sm">{item.name}</p>
      </div>
    ));
  };

  useBodyClassName('bg-slate-50');

  React.useEffect(() => {
    if (flag === 'unsubscribe') {
      setAlertValue({
        msg: '退订成功',
        severity: 'success',
        autoHideDuration: 5000,
      });
    }
  }, []);

  return (
    <div className="-mt-[212px] flex justify-center items-start">
      <div className="grow mr-8 z-10 min-w-0">
        <div className="bg-white rounded-lg px-10 py-6 mb-6">
          <div className="w-30 h-30 overflow-hidden rounded-lg relative border border-solid border-slate-300">
            <Image
              src={logo || defaultCompanyLogo}
              alt={`${name}-logo`}
              fill
              style={{
                objectFit: 'contain',
              }}
            />
          </div>
          <h1 className="font-semibold mt-6 text-4pxl">{name}</h1>
          <p className="mt-3 text-sm leading-5.5 font-normal">
            {joinStrFromList(
              [
                COMPANY_SIZE_MAP[size],
                industry?.name,
                financingStage
                  ? COMPANY_FINANCING_STAGE_MAP[financingStage]
                  : '',
              ],
              ' · ',
            )}
          </p>
          <div
            className={`flex gap-12 ${
              (workSchedule.start_time && workSchedule.end_time) ||
              workSchedule.rest_type ||
              workSchedule.work_over
                ? 'mt-3'
                : ''
            } text-sm leading-5.5 font-normal`}
          >
            {workSchedule.start_time && workSchedule.end_time && (
              <div className="flex items-center gap-3">
                <>
                  <WatchLaterIcon className="text-slate-400" />
                  {moment(workSchedule.start_time).format('HH:mm') +
                    ' - ' +
                    moment(workSchedule.end_time).format('HH:mm')}
                </>
              </div>
            )}
            {(workSchedule.rest_type || workSchedule.work_over) && (
              <div className="flex items-center gap-3">
                <LocalCafeIcon className="text-slate-400" />
                {REST_TYPE_MAP[workSchedule.rest_type]}{' '}
                {WORK_OVERTIME_TYPE_MAP[workSchedule.work_over]}
              </div>
            )}
          </div>
        </div>
        {(description || images) && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">公司优势</p>
            <div
              dangerouslySetInnerHTML={{
                __html: description?.replace(
                  /(\n|\r|\r\n)/g,
                  '<br/>',
                ) as string,
              }}
              className="text-sm leading-5.5 font-normal mb-6 break-words"
            />
            <div>
              <Swiper
                slidesPerView={2}
                spaceBetween={12}
                modules={[Autoplay, Virtual]}
                speed={500}
                autoplay={{ disableOnInteraction: true }}
                loop={images?.length > 2}
                centeredSlides={true}
              >
                {images?.map((image, index) => (
                  <SwiperSlide
                    key={image + index}
                    virtualIndex={index}
                    className="grid relative comm-border-primary rounded-lg overflow-hidden h-60"
                  >
                    <Image
                      src={image}
                      alt="company photo"
                      width={480}
                      fill
                      style={{
                        objectFit: 'cover',
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        )}
        {videos && videos.length > 0 && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">公司展示</p>
            {videos.map((item, index) => (
              <div
                key={index}
                className="comm-border-primary rounded-lg w-full h-full overflow-hidden odd:mb-4"
              >
                <video
                  key={index}
                  src={item}
                  controls
                  width="100%"
                  height="100%"
                />
              </div>
            ))}
          </div>
        )}
        {products.length > 0 && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">公司产品</p>
            <div className="grid gap-6 grid-cols-2 mb-12">
              {renderCompanyCard(products)}
            </div>
          </div>
        )}
        {teamExecutives.length > 0 && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">公司高管</p>
            <div className="grid gap-6 grid-cols-2">
              {renderCompanyCard(teamExecutives)}
            </div>
          </div>
        )}
        {culturePreferenceType && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">CHAM团队文化偏好</p>
            <div className="flex justify-center mt-16">
              <ResultChart
                teamTypePreference={culturePreferenceType}
                chartSize={500}
              />
            </div>
          </div>
        )}
        {benefits.length > 0 && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">公司福利</p>
            <div className="grid gap-6 grid-cols-4">{renderBenefits()}</div>
          </div>
        )}
        {location.location && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">地址</p>
            <div className="h-16 rounded-lg text-sm flex items-center gap-3 pl-7 bg-blue-50/50">
              <LocationOnIcon />
              {location.location?.name}
              {location.address}
            </div>
          </div>
        )}
        {opportunityJobs.length > 0 && (
          <>
            <MarketingBanner letter={letter} />
            <div className="bg-white rounded-lg px-10 py-6 mb-6">
              <p className="text-xl mb-6 font-semibold">热招职位</p>
              <div className="grid gap-4 grid-cols-2">
                {opportunityJobs.slice(0, 10).map((item) => (
                  <JobOpportunityCard key={item.id} data={item} />
                ))}
              </div>
            </div>
          </>
        )}
        {relatedJobs.length > 0 && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">相关职位</p>
            <div className="grid gap-4 grid-cols-2">
              {relatedJobs.slice(0, 6).map((item) => (
                <JobOpportunityCard key={item.id} data={item} />
              ))}
            </div>
          </div>
        )}
        {cities.length > 0 && (
          <div className="bg-white rounded-lg px-10 py-6 mb-6">
            <p className="text-xl mb-6 font-semibold">热门城市</p>
            <div className="grid gap-4 grid-cols-2">
              {cities.slice(0, 4).map((item) => (
                <CityCard key={item.id} {...item} />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="w-[310px] flex-shrink-0">
        <SubscribeJob
          isJobTitle={false}
          initValue={{ id, name, search_type: SEARCH_TYPE.COMPANY }}
        />
      </div>
    </div>
  );
};

export default CompanyDetail;
